import React from 'react';

import useUnitSystem from '../../hooks/useUnitSystem';
import FieldHelp, { elementHelpText } from '../common/FieldHelp';
import MeasurementValue from '../common/MeasurementConversions';
import ValueWithAction, { hasValueAction } from '../common/ValueAction';
import MaskedValue from '../common/MaskedValue';
import { isMaskedElement } from '../../utils/maskedElements';
import FilterValueTrigger, { FilterValueLink } from '../common/FilterValueTrigger';
import MarkdownContent from '../common/MarkdownContent';
import { formatDateRange } from '../../utils/dateDisplay';
import { formatDateEntry, MULTI_DATE_TYPES, normalizeDateList } from '../../utils/dateOptions';
import { elementLabel, formatValue, getChoiceMeta, rangeLabel } from './itemValueDisplay';
import UserValue from '../users/UserValue';
import { isEmptyUserValue as isEmptyUser } from '../../utils/userElements';
import LookupUnavailable from '../common/LookupUnavailable';
import { QualifiedLookupBadges, useQualifiedLookups } from '../common/LookupRefs';
import { extractLookupIds } from '../../utils/lookups';
import { useNotReadableIds } from '../../utils/linkedRecords';
import ChoiceChips from './ChoiceChips';
import { isChoiceList } from '../../utils/choiceValues';
import FileValue from '../common/FileValue';
import { filesOf } from '../../utils/fileValues';
import ConfirmationsValue from './ConfirmationsValue';
import { isConfirmationsElement } from '../../utils/confirmationValues';
import { isRepeatingGroup } from '../../config/repeatingGroups';
import RepeatingGroupValue from './RepeatingGroupValue';

/**
 * One read-only field: micro-label over the full, untruncated value.
 *
 * Shared by the detail pane and the public shared-item page so both render an
 * element the same way — choice dot/icon, markdown for rich text, the shared
 * formatters for everything else. The element's help text, when the card
 * author wrote any, rides the micro-label the same way it rides the entry
 * form's label (hover tooltip, "?" popover) — see FieldHelp.
 *
 * `resolveLookupValue` turns a card-lookup's stored id into a label. The
 * public page has no lookup pool to resolve against (that fetch is
 * authenticated), so it passes a resolver that returns nothing and the field
 * falls back to the same em-dash an empty value gets — never a raw id.
 *
 * `unitSystem` overrides the signed-in user's measurement preference; the
 * public page passes 'default' so a shared record reads as the card defines
 * it, whoever is looking.
 *
 * A "Me too" element (KPR-390) is the one row that WRITES: `confirmation` is
 * its state from the read view's one GET (hooks/useItemConfirmations), and
 * `onConfirmationToggle` the press. Without them — the public page, a
 * printed sheet — it reads as its count alone.
 */
export default function ItemFieldRow({
	element,
	value,
	resolveLookupValue,
	unitSystem = null,
	rangeEnd = null,
	rangeEndValue = undefined,
	item = null,
	canDownload = true,
	confirmation = null,
	onConfirmationToggle = null,
	// A COLUMN of a repeating group (RepeatingGroupValue): `bare` is the value
	// alone, for a table cell under its column head; `facet={false}` keeps the
	// filter-by-this-value trigger off a value that is not a field of the card.
	bare = false,
	facet = true,
	// Where an entry's own address is built from (the group's links).
	entryLinks = null,
	// Whether the reader sees file elements at all (the collection's attachments
	// gate, ItemReadView): a group's FILE columns follow it (KPR-934).
	filesVisible = true,
}) {
	const preferredUnitSystem = useUnitSystem();
	const notReadable = useNotReadableIds();
	// A card lookup's references, less the ones this reader can no longer
	// open (below); of those, the ones into ANOTHER collection read as their
	// qualified badge (LookupRefs). Asked for every field — a hook cannot sit
	// in the branch — and answering nothing for any other type.
	const isLookup = element?.dataType === 'card-lookup';
	const lookupIds = isLookup ? extractLookupIds(value) : [];
	const lookupGone = lookupIds.filter(id => notReadable.has(id));
	const lookupReadable = lookupIds.filter(id => !notReadable.has(id));
	const lookupShown = lookupGone.length ? (Array.isArray(value) ? lookupReadable : lookupReadable[0]) : value;
	const { qualified: foreignRefs, localValue: lookupLocal } = useQualifiedLookups(isLookup ? lookupShown : null, resolveLookupValue);
	const prefs = { unitSystem: unitSystem || preferredUnitSystem };
	const choiceMeta = getChoiceMeta(element, value);
	// A range reads as one value under both labels, "Departs / Returns" (KPR-626).
	// A group reads under its plural ("Ingredients"), as on the form.
	const label = rangeEnd
		? rangeLabel(element, rangeEnd)
		: (isRepeatingGroup(element) && element?.label?.plural ? element.label.plural : elementLabel(element));
	// A date element that holds several dates lists EVERY one, as chips on one
	// wrapping row — the read view is where the whole value belongs (a tile
	// has room for "Sep 9 +2" and says so). Ascending, the canonical order.
	const dateList = (!rangeEnd && Array.isArray(value) && MULTI_DATE_TYPES.includes(element?.dataType))
		? normalizeDateList(element?.dataType, value)
		: null;
	let node;
	// A masked element (KPR-1348): its mask, with Show and Copy. The reveal
	// belongs to this item: moving to another item masks it again.
	const masked = isMaskedElement(element);
	if (masked) {
		node = (
			<div className="item-pane__value">
				<MaskedValue element={element} value={value} prefs={prefs} resetKey={item?._id} />
			</div>
		);
	} else if (dateList) {
		node = (
			<div className="item-pane__value item-pane__value--chips" data-testid="multi-date-chips">
				{dateList.length
					? dateList.map((entry, index) => (
						<span className="value-chip" key={`${entry}-${index}`}>{formatDateEntry(element, entry)}</span>
					))
					: '—'}
			</div>
		);
	} else if (rangeEnd) {
		// A range is two elements and ONE reading: "Sep 9 – Sep 12 · 3 days",
		// "Sep 9" when it starts and ends on a day, "Sep 9 →" while the end is
		// still open, "→ Sep 12" with only the end. The end element has no row
		// of its own (ItemReadView).
		node = (
			<div className="item-pane__value" data-testid="range-value">
				{formatDateRange(value, rangeEndValue, element?.dataType) || '—'}
			</div>
		);
	} else if (isChoiceList(element, value)) {
		// A Choice that allows multiple values lists EVERY entry, as chips on
		// one wrapping row, in the choices' order (data types PR 5).
		node = (
			<div className="item-pane__value item-pane__value--chips">
				<ChoiceChips element={element} value={value} />
			</div>
		);
	} else if (element?.dataType === 'file') {
		// A file element (docs/plans/data-types-file-element.md §6): photos at
		// content width, documents as the chip with Download — from the item
		// read's `files` facts. `item` carries them; an id with none (the gate
		// off, the file gone) shows nothing, and an element holding nothing
		// readable reads as empty. Whether the row shows at all is the read
		// view's call (utils/fileValues fileElementVisible).
		node = (
			<div className="item-pane__value">
				{filesOf(item, value).length ? <FileValue item={item} value={value} canDownload={canDownload} /> : '—'}
			</div>
		);
	} else if (isConfirmationsElement(element)) {
		// "Me too": the count, and beside it the element's own toggle — its
		// label as the words, filled once the person said it (ConfirmationsValue).
		node = (
			<ConfirmationsValue
				element={element}
				value={value}
				state={confirmation}
				onToggle={onConfirmationToggle}
				itemId={item?._id || null}
			/>
		);
	} else if (isRepeatingGroup(element)) {
		// A repeating group: its entries, as a table-like list or cards
		// (components/items/RepeatingGroupValue.js; platform-standards § 4).
		node = (
			<RepeatingGroupValue
				element={element}
				value={value}
				resolveLookupValue={resolveLookupValue}
				unitSystem={unitSystem}
				entryLinks={entryLinks}
				item={item}
				canDownload={canDownload}
				filesVisible={filesVisible}
			/>
		);
	} else if (element?.dataType === 'rich-text') {
		node = <MarkdownContent markdown={(value || '').toString()} />;
	} else if (element?.dataType === 'user') {
		// People, not ids: the account chip, or the linked record's badge.
		node = (
			<div className="item-pane__value item-pane__value--users">
				<UserValue element={element} value={value} size="xs" /> {isEmptyUser(value) ? '—' : null}
			</div>
		);
	} else if (element?.dataType === 'card-lookup') {
		// ONE reading of a reference, here and on a printed sheet: the shared
		// renderer, which omits an id the pool has not delivered (utils/lookups
		// resolveLookupText) and leaves the same em-dash an empty value gets.
		// This row used to print `resolveLookupValue(value) || value` — so on a
		// pool miss it printed the raw id, while a label-less page block
		// rendering the SAME element through formatValue printed nothing. Two
		// readings of one value on one sheet, one of them an ObjectId.
		// A reference the server says this reader can no longer open (the
		// tombstone a collection link leaves — X-Not-Readable) says so, muted
		// and unlinked, rather than vanishing into the em-dash.
		const gone = lookupGone;
		const shown = lookupShown;
		const text = formatValue(element, foreignRefs.length ? lookupLocal : shown, { ...prefs, resolveLookupValue });
		// The reference itself is the door: inside a list, clicking the title
		// asks for every record that points at the same item. A reference
		// into another collection says whose it is: the qualified badge.
		node = (
			<div className={`item-pane__value${gone.length || foreignRefs.length ? ' item-pane__value--chips' : ''}`}>
				{text || foreignRefs.length || !gone.length ? (
					<FilterValueLink element={element} value={shown} resolveLookupValue={resolveLookupValue}>
						{foreignRefs.length ? (
							<span className="lookup-refs" data-testid="lookup-refs">
								{text ? <span className="lookup-refs__text">{text}</span> : null}
								<QualifiedLookupBadges refs={foreignRefs} size="sm" />
							</span>
						) : (text || '—')}
					</FilterValueLink>
				) : null}
				{gone.map(id => <LookupUnavailable key={id} />)}
			</div>
		);
	} else if (hasValueAction(element?.dataType)) {
		// A phone, an email, a location or a URL: the formatted value plus
		// the one thing you do with it (docs/platform-standards.md §4,
		// "Values carry their actions").
		node = (
			<div className="item-pane__value">
				<ValueWithAction element={element} value={value} />
			</div>
		);
	} else if (element?.dataType === 'measurement') {
		// The formatted value plus the conversions affordance (see
		// MeasurementConversions); the same em-dash when empty.
		node = (
			<div className="item-pane__value">
				<MeasurementValue element={element} value={value} unitSystem={prefs.unitSystem} />
			</div>
		);
	} else {
		const ChoiceIcon = choiceMeta.icon;
		node = (
			<div className="item-pane__value">
				{choiceMeta.color ? (
					<span className="item-element__value-dot" style={{ backgroundColor: choiceMeta.color }} />
				) : null}
				{ChoiceIcon ? <span className="item-element__value-icon"><ChoiceIcon /></span> : null}
				{choiceMeta.label || formatValue(element, value, prefs) || '—'}
			</div>
		);
	}
	if (bare) return node;
	return (
		<div className="item-pane__field">
			<FieldHelp text={elementHelpText(element)} label={label}>
				<div className="micro-label">{label}</div>
			</FieldHelp>
			{node}
			{/* Inside a list, a value is also a facet of it — "show me the rest
			    of these" (docs/platform-standards.md §5). A lookup already has
			    its title as the door, and a range is two values, not one. */}
			{/* A file is no facet: nothing filters by an attachment id. A lookup's title and its menu are two doors to the same filter. */}
			{/* Nor is a masked value: filtering by it would write the code into the list's address. */}
			{!facet || masked || isRepeatingGroup(element) || rangeEnd || element?.dataType === 'file' ? null : (
				<FilterValueTrigger element={element} value={value} resolveLookupValue={resolveLookupValue} />
			)}
		</div>
	);
}
